/**
 * Gates the interview edit-save behind a confirm dialog when saving would stop
 * this candidate's stage automation.
 *
 * The decision — "did THIS edit turn the scorecard optional while a downstream
 * automated activity depends on its submission?" — is computed by the caller
 * and passed as `shouldConfirmSave`. When it's false the save proceeds straight
 * through. Mirrors the assessment drawer's stop-automation confirm.
 */
import { useCallback, useRef, useState } from 'react';

interface UseScorecardStopConfirmArgs {
  /** Evaluated at save time. True when saving would stop automation (scorecard
   *  toggled optional in this edit AND a downstream activity depends on it). */
  shouldConfirmSave: () => boolean;
}

export interface ScorecardStopConfirmState {
  /** Wraps a save — opens the dialog when it would stop automation, otherwise
   *  calls `proceed` immediately. */
  guardSave: (_proceed: () => void) => void;
  /** Call when the user confirms in the dialog — runs the deferred save. */
  confirmStop: () => void;
  /** Call when the user cancels — dialog dismissed, save abandoned. */
  cancelStop: () => void;
  isStopDialogOpen: boolean;
}

export function useScorecardStopConfirm({
  shouldConfirmSave,
}: UseScorecardStopConfirmArgs): ScorecardStopConfirmState {
  const pendingSave = useRef<(() => void) | null>(null);
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);

  const guardSave = useCallback(
    (proceed: () => void) => {
      if (shouldConfirmSave()) {
        pendingSave.current = proceed;
        setIsStopDialogOpen(true);
        return;
      }
      proceed();
    },
    [shouldConfirmSave]
  );

  const confirmStop = useCallback(() => {
    setIsStopDialogOpen(false);
    const save = pendingSave.current;
    pendingSave.current = null;
    save?.();
  }, []);

  const cancelStop = useCallback(() => {
    pendingSave.current = null;
    setIsStopDialogOpen(false);
  }, []);

  return { guardSave, confirmStop, cancelStop, isStopDialogOpen };
}
